import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  ParticipationStatus,
  StudyType,
  useParticipation,
  useUndoNotInterestedParticipation,
} from '@app/api';
import { useLocale } from '@app/i18n';

import { Button, NotFound, PublicBackground } from '@app/core';
import { RequireValidation } from './components/RequireValidation';
import { BookForMeetingPage } from './screens/BookForMeetingPage';
import { SuccessScreen } from './components/SuccessScreen';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { AppointmentSummary } from './components/AppointmentSummary';
import { TagsConfirmedParticipationHeader } from './components/TagsConfirmedParticipationHeader';
import { ConfirmedParticipationBottomHeader } from './components/ConfirmedParticipationBottomHeader';
import { ParticipationInstructionUnmoderated } from './components/ParticipationInstructionUnmoderated';
import { useTranslation } from 'react-i18next';
import { OutOfQuota } from './components/OutOfQuota';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.marginBase * 2,
    marginTop: theme.marginBase * 3,
  },
}));

export const ParticipationSummaryIndex = () => {
  const classes = useStyles();
  const { id: idOrRef } = useParams();
  const { data: participation } = useParticipation(idOrRef);
  const location = useLocation();
  const { t } = useTranslation();

  const { mutate: undoNotInterested } = useUndoNotInterestedParticipation(
    participation?.id,
  );

  const locale =
    participation && participation.session && participation.session.locale;
  useLocale(locale);

  const session = participation?.session;

  if (!participation) {
    return <NotFound />;
  }

  switch (participation.status) {
    case ParticipationStatus.CANCELED:
      return (
        <NotFound
          title={t('booked.cancelledError.title', {
            context: session?.studyType,
          })}
          text={t('booked.cancelledError.message')}
        />
      );
    case ParticipationStatus.NO_SHOW:
    case ParticipationStatus.BOOKED:
    case ParticipationStatus.PARTICIPATED:
    case ParticipationStatus.PAYMENT_REQUESTED:
    case ParticipationStatus.PROCESSING_PAYMENT:
    case ParticipationStatus.PAYED: {
      return (
        <PublicBackground
          customContent
          tagsChildren={
            <TagsConfirmedParticipationHeader participation={participation} />
          }
          bottomChildren={
            <ConfirmedParticipationBottomHeader participation={participation} />
          }
          withMessagesPane
          participation={participation}
        >
          {session && session.studyType === StudyType.MODERATED ? (
            <AppointmentSummary participation={participation} />
          ) : (
            <ParticipationInstructionUnmoderated
              participation={participation}
            />
          )}
        </PublicBackground>
      );
    }

    case ParticipationStatus.IN_PROGRESS:
    case ParticipationStatus.ACCEPTED:
      return session && session.studyType !== StudyType.MODERATED ? (
        <PublicBackground
          customContent
          tagsChildren={
            <TagsConfirmedParticipationHeader participation={participation} />
          }
          bottomChildren={
            <ConfirmedParticipationBottomHeader participation={participation} />
          }
          participation={participation}
        >
          <ParticipationInstructionUnmoderated participation={participation} />
        </PublicBackground>
      ) : (
        <BookForMeetingPage participation={participation} />
      );
    case ParticipationStatus.APPLIED:
      return (
        <PublicBackground withMessagesPane participation={participation}>
          <RequireValidation />
        </PublicBackground>
      );
    case ParticipationStatus.DECLINED:
      return (
        <PublicBackground withMessagesPane participation={participation}>
          <RequireValidation declined />
        </PublicBackground>
      );
    case ParticipationStatus.OUT_OF_QUOTA:
      return participation.acceptedAt ? (
        <PublicBackground>
          <OutOfQuota />
        </PublicBackground>
      ) : (
        <PublicBackground>
          <RequireValidation declined />
        </PublicBackground>
      );
    case ParticipationStatus.NOT_INTERESTED:
      return (
        <PublicBackground>
          <SuccessScreen
            title={t('application.notInterested.title')}
            description={t('application.notInterested.description')}
          >
            <div className={classes.buttonContainer}>
              <Button
                text={t('application.notInterested.recover')}
                variant="muted"
                onClick={() => undoNotInterested()}
              />
              <Button
                text={t('application.notInterested.button')}
                to="/profile"
              />
            </div>
          </SuccessScreen>
        </PublicBackground>
      );
    case ParticipationStatus.INVITED:
    default:
      return <Navigate to="booking" replace state={location.state} />;
  }
};
